/**
 * ContractSignPage — pagina PUBBLICA di firma elettronica (/firma/:token).
 * ★ Fase 8 — wizard: 1) Leggi → 2) Verifica email (OTP) → 3) Documento + selfie
 * → 4) Consenso + firma → 5) Conferma. Nessun login richiesto.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  ShieldCheck, Mail, Camera, PenLine, CheckCircle2, Loader2, IdCard,
  ChevronRight, FileSignature, AlertTriangle, FileText, Download,
} from 'lucide-react';
import { SafeHtml } from '../lib/sanitize';
import { SignatureCanvas } from '../components/contracts/SignatureCanvas';
import { IdentityStepV2 } from '../components/contracts/IdentityStepV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/contracts`;

// ★ Fase 26 — Pagina di firma multilingua: la lingua arriva dal backend
// (contracts.language = tenant CRM di invio). Fallback: italiano.
const TR = {
  it: {
    steps: { read: 'Documento', otp: 'Verifica email', identity: 'Identità', sign: 'Firma' },
    notFound: 'Documento non trovato o link scaduto.',
    notAvailable: 'Documento non disponibile',
    protected: 'Firma elettronica protetta',
    docNo: 'Documento n.',
    introPre: 'Gentile', introPost: 'ti ha inviato questo documento per la firma.',
    signedTitle: 'Documento firmato ✓',
    signedThanks: 'Grazie', signedOn: 'il',
    signedBody: "La tua firma è stata registrata. Dopo la verifica dell'identità da parte del nostro team riceverai via email la copia PDF firmata con tutte le evidenze.",
    locale: 'it-IT',
    attachments: 'Allegati al contratto',
    attachmentsHint: 'Ti consigliamo di leggere gli allegati prima di procedere alla firma.',
    continueBtn: 'Ho letto il documento — procedi alla firma',
    otpTitle: 'Verifica la tua email',
    otpDescPre: 'Invieremo un codice di 6 cifre a', otpDescPost: 'per confermare la tua identità.',
    otpSend: 'Inviami il codice',
    otpVerify: 'Verifica codice',
    otpResend: 'Non è arrivato? Invia di nuovo',
    otpSentToast: 'Codice inviato: controlla la tua email (anche lo spam).',
    otpSendErr: 'Errore invio codice',
    otpOkToast: 'Email verificata!',
    otpBadCode: 'Codice non valido',
    idTitle: 'Verifica della tua identità',
    idDesc: "Carica una foto del tuo documento d'identità e un autoscatto (selfie). Servono a dare pieno valore probatorio alla firma.",
    idDocLabel: "1 · Documento d'identità (fronte)",
    idSelfieLabel: '2 · Selfie (il tuo volto ben visibile)',
    idTypeTitle: 'Che documento usi?',
    idPassport: 'Passaporto', idIdCard: "Carta d'identità",
    idPassportFront: '1 · Passaporto — pagina con la foto',
    idCardFront: "1 · Carta d'identità — fronte", idCardBack: "2 · Carta d'identità — retro",
    idSelfieDocLabel: 'Selfie con il documento in mano',
    idSelfieDocHint: 'Tieni il documento accanto al viso: volto e documento devono vedersi bene nella stessa foto.',
    idDescV2: "Scegli il documento, fotografalo e scatta un selfie tenendolo in mano. Servono a dare pieno valore probatorio alla firma.",
    idTypeMissing: 'Scegli il tipo di documento.', idBackMissing: "Carica anche il retro della carta d'identità.",
    idTap: 'Tocca per scattare o caricare la foto',
    idSubmit: 'Conferma documenti',
    idBusy: 'Verifica in corso…',
    idPrivacy: "Le immagini sono conservate in modo riservato e usate solo per la verifica dell'identità.",
    idMissing: 'Carica documento e selfie.',
    idOkToast: 'Documenti ricevuti!',
    idErr: 'Errore caricamento documenti',
    imgErr: 'Immagine non leggibile, riprova.',
    signTitle: 'Firma il documento',
    signBtn: 'Firma e concludi',
    signBusy: 'Registrazione firma…',
    signOkToast: 'Documento firmato!',
    signErr: 'Errore durante la firma',
    consentErr: 'Devi accettare il consenso alla firma elettronica.',
    sigEmptyErr: 'Disegna la tua firma nel riquadro.',
    footer: 'Firma elettronica con evidenze (OTP, documento, selfie, hash) — art. 20 D.Lgs. 82/2005 · Reg. (UE) 910/2014.',
  },
  es: {
    steps: { read: 'Documento', otp: 'Verificar email', identity: 'Identidad', sign: 'Firma' },
    notFound: 'Documento no encontrado o enlace caducado.',
    notAvailable: 'Documento no disponible',
    protected: 'Firma electrónica protegida',
    docNo: 'Documento n.º',
    introPre: 'Estimado/a', introPost: 'te ha enviado este documento para su firma.',
    signedTitle: 'Documento firmado ✓',
    signedThanks: '¡Gracias', signedOn: 'el',
    signedBody: 'Tu firma ha sido registrada. Tras la verificación de identidad por parte de nuestro equipo recibirás por email la copia PDF firmada con todas las evidencias.',
    locale: 'es-ES',
    attachments: 'Anexos al contrato',
    attachmentsHint: 'Te recomendamos leer los anexos antes de proceder a la firma.',
    continueBtn: 'He leído el documento — proceder a la firma',
    otpTitle: 'Verifica tu email',
    otpDescPre: 'Enviaremos un código de 6 dígitos a', otpDescPost: 'para confirmar tu identidad.',
    otpSend: 'Enviarme el código',
    otpVerify: 'Verificar código',
    otpResend: '¿No ha llegado? Enviar de nuevo',
    otpSentToast: 'Código enviado: revisa tu email (también el spam).',
    otpSendErr: 'Error al enviar el código',
    otpOkToast: '¡Email verificado!',
    otpBadCode: 'Código no válido',
    idTitle: 'Verificación de tu identidad',
    idDesc: 'Sube una foto de tu documento de identidad y una autofoto (selfie). Sirven para dar pleno valor probatorio a la firma.',
    idDocLabel: '1 · Documento de identidad (anverso)',
    idSelfieLabel: '2 · Selfie (tu rostro bien visible)',
    idTypeTitle: '¿Qué documento usas?',
    idPassport: 'Pasaporte', idIdCard: 'Documento de identidad (DNI)',
    idPassportFront: '1 · Pasaporte — página con la foto',
    idCardFront: '1 · Documento de identidad — anverso', idCardBack: '2 · Documento de identidad — reverso',
    idSelfieDocLabel: 'Selfie con el documento en la mano',
    idSelfieDocHint: 'Sostén el documento junto a la cara: rostro y documento deben verse bien en la misma foto.',
    idDescV2: 'Elige el documento, fotografíalo y hazte un selfie sosteniéndolo en la mano. Sirven para dar pleno valor probatorio a la firma.',
    idTypeMissing: 'Elige el tipo de documento.', idBackMissing: 'Sube también el reverso del documento de identidad.',
    idTap: 'Toca para hacer o subir la foto',
    idSubmit: 'Confirmar documentos',
    idBusy: 'Verificación en curso…',
    idPrivacy: 'Las imágenes se conservan de forma confidencial y se usan solo para la verificación de identidad.',
    idMissing: 'Sube el documento y el selfie.',
    idOkToast: '¡Documentos recibidos!',
    idErr: 'Error al subir los documentos',
    imgErr: 'Imagen no legible, inténtalo de nuevo.',
    signTitle: 'Firma el documento',
    signBtn: 'Firmar y concluir',
    signBusy: 'Registrando la firma…',
    signOkToast: '¡Documento firmado!',
    signErr: 'Error durante la firma',
    consentErr: 'Debes aceptar el consentimiento a la firma electrónica.',
    sigEmptyErr: 'Dibuja tu firma en el recuadro.',
    footer: 'Firma electrónica con evidencias (OTP, documento, selfie, hash) — Reg. (UE) 910/2014 (eIDAS).',
  },
  en: {
    steps: { read: 'Document', otp: 'Verify email', identity: 'Identity', sign: 'Sign' },
    notFound: 'Document not found or link expired.',
    notAvailable: 'Document unavailable',
    protected: 'Protected electronic signature',
    docNo: 'Document no.',
    introPre: 'Dear', introPost: 'has sent you this document for signature.',
    signedTitle: 'Document signed ✓',
    signedThanks: 'Thank you', signedOn: 'on',
    signedBody: 'Your signature has been recorded. After identity verification by our team you will receive the signed PDF copy with all the evidence by email.',
    locale: 'en-GB',
    attachments: 'Contract attachments',
    attachmentsHint: 'We recommend reading the attachments before proceeding to sign.',
    continueBtn: 'I have read the document — proceed to sign',
    otpTitle: 'Verify your email',
    otpDescPre: 'We will send a 6-digit code to', otpDescPost: 'to confirm your identity.',
    otpSend: 'Send me the code',
    otpVerify: 'Verify code',
    otpResend: "Didn't arrive? Send again",
    otpSentToast: 'Code sent: check your email (including spam).',
    otpSendErr: 'Error sending code',
    otpOkToast: 'Email verified!',
    otpBadCode: 'Invalid code',
    idTitle: 'Identity verification',
    idDesc: 'Upload a photo of your identity document and a selfie. They give the signature full evidentiary value.',
    idDocLabel: '1 · Identity document (front)',
    idSelfieLabel: '2 · Selfie (your face clearly visible)',
    idTypeTitle: 'Which document are you using?',
    idPassport: 'Passport', idIdCard: 'Identity card',
    idPassportFront: '1 · Passport — photo page',
    idCardFront: '1 · Identity card — front', idCardBack: '2 · Identity card — back',
    idSelfieDocLabel: 'Selfie holding the document',
    idSelfieDocHint: 'Hold the document next to your face: both must be clearly visible in the same photo.',
    idDescV2: 'Choose the document, take a photo of it and a selfie holding it. They give the signature full evidentiary value.',
    idTypeMissing: 'Choose the document type.', idBackMissing: 'Please also upload the back of the identity card.',
    idTap: 'Tap to take or upload the photo',
    idSubmit: 'Confirm documents',
    idBusy: 'Verifying…',
    idPrivacy: 'Images are stored confidentially and used only for identity verification.',
    idMissing: 'Upload document and selfie.',
    idOkToast: 'Documents received!',
    idErr: 'Error uploading documents',
    imgErr: 'Image unreadable, please try again.',
    signTitle: 'Sign the document',
    signBtn: 'Sign and finish',
    signBusy: 'Recording signature…',
    signOkToast: 'Document signed!',
    signErr: 'Error while signing',
    consentErr: 'You must accept the electronic signature consent.',
    sigEmptyErr: 'Draw your signature in the box.',
    footer: 'Electronic signature with evidence (OTP, document, selfie, hash) — Reg. (EU) 910/2014 (eIDAS).',
  },
};

const pickLang = (l) => (TR[l] ? l : 'it');

/** Ridimensiona un'immagine lato client (max 1400px, JPEG q0.85) → base64. */
async function fileToJpegB64(file, maxDim = 1400) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
}

const STEPS = [
  { key: 'read', icon: FileSignature },
  { key: 'otp', icon: Mail },
  { key: 'identity', icon: IdCard },
  { key: 'sign', icon: PenLine },
];

function Stepper({ current, labels }) {
  return (
    <div className="flex items-center justify-center gap-1 md:gap-2 flex-wrap">
      {STEPS.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'todo';
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] md:text-xs font-semibold ${
              state === 'done' ? 'bg-emerald-100 text-emerald-800'
                : state === 'active' ? 'bg-[var(--gp-forest,#0C3C2D)] text-[var(--gp-gold,#E8C874)]'
                  : 'bg-slate-100 text-slate-400'
            }`}>
              {state === 'done' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
              {labels[s.key]}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ContractSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  // Identity
  const [docImg, setDocImg] = useState(null);     // {b64, preview}
  const [selfieImg, setSelfieImg] = useState(null);
  const [idServerError, setIdServerError] = useState('');
  // Sign
  const [consent, setConsent] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const sigRef = useRef(null);

  // ★ Fase 26 — lingua: dal contratto se caricato, altrimenti dal browser
  const browserLang = (navigator.language || 'it').slice(0, 2).toLowerCase();
  const T = TR[pickLang(data?.language || browserLang)];

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/${token}`);
      setData(d);
      if (d.signed) setStep(4);
      else if (d.identity_done) setStep(3);
      else if (d.otp_verified) setStep(2);
      else setStep(0);
    } catch (e) {
      setError(e?.response?.data?.detail || TR[pickLang(browserLang)].notFound);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const requestOtp = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/${token}/otp/request`);
      setOtpSent(true);
      toast.success(T.otpSentToast);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T.otpSendErr);
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/${token}/otp/verify`, { code: otpCode });
      toast.success(T.otpOkToast);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T.otpBadCode);
    } finally { setBusy(false); }
  };

  const onPickImage = async (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await fileToJpegB64(file);
      setter({ b64, preview: `data:image/jpeg;base64,${b64}` });
    } catch {
      toast.error(T.imgErr);
    }
  };

  const uploadIdentity = async (v2Payload) => {
    const payload = v2Payload || { doc_image_b64: docImg?.b64, selfie_image_b64: selfieImg?.b64 };
    if (!payload.doc_image_b64 || !payload.selfie_image_b64) { toast.error(T.idMissing); return; }
    setBusy(true);
    setIdServerError('');
    try {
      await axios.post(`${API}/${token}/identity`, payload, { timeout: 90000 });
      toast.success(T.idOkToast);
      setStep(3);
    } catch (e) {
      const msg = e?.response?.data?.detail || T.idErr;
      if (e?.response?.status === 422 || e?.response?.status === 400) setIdServerError(typeof msg === 'string' ? msg : T.idErr);
      toast.error(typeof msg === 'string' ? msg : T.idErr);
    } finally { setBusy(false); }
  };

  const sign = async () => {
    if (!consent) { toast.error(T.consentErr); return; }
    if (sigRef.current?.isEmpty()) { toast.error(T.sigEmptyErr); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/${token}/sign`, {
        consent: true,
        signature_b64: sigRef.current.toDataURL(),
      }, { timeout: 60000 });
      setStep(4);
      await load();
      toast.success(T.signOkToast);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T.signErr);
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f4f1e8] grid place-items-center p-6">
        <div className="max-w-md text-center bg-white rounded-2xl border border-[#e2ddcc] p-8">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <div className="font-serif text-xl text-[#0C3C2D]">{T.notAvailable}</div>
          <p className="text-sm text-slate-500 mt-2" data-testid="sign-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f4f1e8] p-6 max-w-2xl mx-auto space-y-4 pt-16">
        <Skeleton className="h-10 w-2/3" /><Skeleton className="h-64" /><Skeleton className="h-10" />
      </div>
    );
  }

  const alreadySigned = data.signed || step >= 4;

  return (
    <div className="min-h-screen bg-[#f4f1e8]" data-testid="contract-sign-page">
      <Toaster position="top-center" richColors />
      {/* Header */}
      <header className="bg-[#0C3C2D] text-white">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#E8C874] font-semibold">
            Green Paradise · HCF
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-white/70">
            <ShieldCheck className="w-4 h-4 text-[#E8C874]" /> {T.protected}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 pt-6 pb-40 md:py-10 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="font-serif text-2xl md:text-3xl text-[#0C3C2D]" data-testid="sign-title">{data.template_name}</h1>
          {data.serial_number && (
            <p className="text-[11px] font-mono text-slate-400" data-testid="sign-serial">{T.docNo} {data.serial_number}</p>
          )}
          <p className="text-sm text-slate-500">
            {T.introPre} <b>{data.recipient_name}</b>, {data.sender_name} {T.introPost}
          </p>
          {!alreadySigned && <Stepper current={step} labels={T.steps} />}
        </div>

        {/* STEP 5 — già firmato / successo */}
        {alreadySigned ? (
          <div className="bg-white rounded-2xl border border-[#e2ddcc] p-8 text-center space-y-3" data-testid="sign-success">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
            <div className="font-serif text-2xl text-[#0C3C2D]">{T.signedTitle}</div>
            <p className="text-sm text-slate-600 max-w-md mx-auto">
              {T.signedThanks} {data.recipient_name}!{' '}
              {data.signed_at ? `${T.signedOn} ${new Date(data.signed_at).toLocaleString(T.locale)} — ` : ''}
              {T.signedBody}
            </p>
          </div>
        ) : (
          <>
            {/* STEP 1 — lettura */}
            {step === 0 && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-[#e2ddcc] p-5 md:p-8 max-h-[52vh] overflow-y-auto prose prose-sm max-w-none" data-testid="sign-content">
                  <SafeHtml html={data.content_html} />
                </div>
                {(data.attachments || []).length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#e2ddcc] p-4 md:p-5" data-testid="sign-attachments">
                    <div className="text-xs font-semibold text-[#0C3C2D] uppercase tracking-wide mb-2">
                      {T.attachments} ({data.attachments.length})
                    </div>
                    <div className="space-y-1.5">
                      {data.attachments.map((a) => (
                        <a
                          key={a.idx}
                          href={`${API}/${token}/attachments/${a.idx}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-lg border border-[#e2ddcc] hover:border-[#0C3C2D]/40 hover:bg-[#faf8f2] px-3 py-2 text-sm text-[#0C3C2D]"
                          data-testid={`sign-attachment-${a.idx}`}
                        >
                          <FileText className="w-4 h-4 shrink-0" />
                          <span className="truncate flex-1">{a.name}</span>
                          <Download className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        </a>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">
                      {T.attachmentsHint}
                    </p>
                  </div>
                )}
                <Button className="w-full h-12 bg-[#0C3C2D] hover:bg-[#0C3C2D]/90 text-[#E8C874] text-base" onClick={() => setStep(1)} data-testid="sign-continue-btn">
                  {T.continueBtn} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {/* STEP 2 — OTP */}
            {step === 1 && (
              <div className="bg-white rounded-2xl border border-[#e2ddcc] p-6 md:p-8 max-w-md mx-auto space-y-4 text-center">
                <Mail className="w-10 h-10 mx-auto text-[#0C3C2D]" />
                <div className="font-serif text-xl text-[#0C3C2D]">{T.otpTitle}</div>
                <p className="text-sm text-slate-500">
                  {T.otpDescPre} <b>{data.recipient_email_masked}</b> {T.otpDescPost}
                </p>
                {!otpSent ? (
                  <Button className="w-full h-11 bg-[#0C3C2D] text-[#E8C874] hover:bg-[#0C3C2D]/90" onClick={requestOtp} disabled={busy} data-testid="otp-request-btn">
                    {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {T.otpSend}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Input
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="••••••"
                      inputMode="numeric"
                      className="text-center text-2xl tracking-[0.5em] h-14 font-mono"
                      data-testid="otp-code-input"
                    />
                    <Button className="w-full h-11 bg-[#0C3C2D] text-[#E8C874] hover:bg-[#0C3C2D]/90" onClick={verifyOtp} disabled={busy || otpCode.length !== 6} data-testid="otp-verify-btn">
                      {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {T.otpVerify}
                    </Button>
                    <button type="button" className="text-xs text-slate-400 underline" onClick={requestOtp} disabled={busy} data-testid="otp-resend-btn">
                      {T.otpResend}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* STEP 3 — documento + selfie (★ Fase 73: v2 = tipo documento + retro carta + selfie col documento) */}
            {step === 2 && data.identity_mode === 'v2' && (
              <IdentityStepV2 T={T} busy={busy} pickImage={onPickImage} onSubmit={uploadIdentity} serverError={idServerError} />
            )}
            {step === 2 && data.identity_mode !== 'v2' && (
              <div className="bg-white rounded-2xl border border-[#e2ddcc] p-6 md:p-8 max-w-lg mx-auto space-y-5">
                <div className="text-center">
                  <IdCard className="w-10 h-10 mx-auto text-[#0C3C2D]" />
                  <div className="font-serif text-xl text-[#0C3C2D] mt-1">{T.idTitle}</div>
                  <p className="text-sm text-slate-500 mt-1">
                    {T.idDesc}
                  </p>
                </div>
                {[
                  { key: 'doc', label: T.idDocLabel, img: docImg, setter: setDocImg, capture: 'environment' },
                  { key: 'selfie', label: T.idSelfieLabel, img: selfieImg, setter: setSelfieImg, capture: 'user' },
                ].map((it) => (
                  <div key={it.key}>
                    <div className="text-xs font-semibold text-slate-600 mb-1.5">{it.label}</div>
                    <label className="block cursor-pointer">
                      <input
                        type="file" accept="image/*" capture={it.capture} className="hidden"
                        onChange={(e) => onPickImage(e, it.setter)}
                        data-testid={`identity-input-${it.key}`}
                      />
                      {it.img ? (
                        <img src={it.img.preview} alt={it.key} className="rounded-xl border-2 border-emerald-300 max-h-44 w-full object-contain bg-slate-50" data-testid={`identity-preview-${it.key}`} />
                      ) : (
                        <div className="rounded-xl border-2 border-dashed border-slate-300 hover:border-[#0C3C2D]/50 p-6 text-center text-slate-400">
                          <Camera className="w-7 h-7 mx-auto mb-1" />
                          <span className="text-xs">{T.idTap}</span>
                        </div>
                      )}
                    </label>
                  </div>
                ))}
                <Button className="w-full h-11 bg-[#0C3C2D] text-[#E8C874] hover:bg-[#0C3C2D]/90" onClick={() => uploadIdentity()} disabled={busy || !docImg || !selfieImg} data-testid="identity-submit-btn">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {T.idBusy}</> : T.idSubmit}
                </Button>
                <p className="text-[10px] text-slate-400 text-center">
                  {T.idPrivacy}
                </p>
              </div>
            )}

            {/* STEP 4 — consenso + firma */}
            {step === 3 && (
              <div className="bg-white rounded-2xl border border-[#e2ddcc] p-6 md:p-8 max-w-lg mx-auto space-y-4">
                <div className="text-center">
                  <PenLine className="w-10 h-10 mx-auto text-[#0C3C2D]" />
                  <div className="font-serif text-xl text-[#0C3C2D] mt-1">{T.signTitle}</div>
                </div>
                <SignatureCanvas ref={sigRef} onChange={setSigEmpty} />
                <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-[#E4C88A] bg-[#FBF3DF] p-3">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#0C3C2D] shrink-0"
                    data-testid="consent-checkbox"
                  />
                  <span className="text-[11px] leading-relaxed text-[#6A4B00]">{data.consent_text}</span>
                </label>
                <Button
                  className="w-full h-12 bg-[#E8C874] text-[#0C3C2D] hover:bg-[#f0d795] text-base font-semibold"
                  onClick={sign}
                  disabled={busy || !consent || sigEmpty}
                  data-testid="sign-submit-btn"
                >
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {T.signBusy}</> : T.signBtn}
                </Button>
              </div>
            )}
          </>
        )}

        <p className="text-center text-[10px] text-slate-400 pb-8">
          {T.footer}
        </p>
      </main>
    </div>
  );
}
